import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Ticket #91 — chargement incrémental d'une fenêtre temporelle glissante.
 *
 * La fenêtre CHARGÉE du Planning et de la vue calendaire des imputations glisse
 * d'un écran à chaque approche d'un bord. Recharger toute la fenêtre à chaque
 * glissement (jusqu'à 350 jours en zoom Mois) faisait clignoter la grille et
 * saturait l'API pour quelques jours réellement nouveaux. Ce hook ne demande
 * que les segments manquants et fusionne leur réponse dans les données déjà
 * présentes.
 *
 * Règles :
 *  - changement de `depsKey` (filtre, périmètre…) : les données ne valent plus
 *    rien, rechargement complet BLOQUANT (`loading` = true, overlay autorisé) ;
 *  - changement de `reloadKey` (mutation, SSE) : rechargement complet
 *    SILENCIEUX — la grille reste affichée pendant l'appel ;
 *  - glissement de fenêtre : seuls les jours non couverts (ni chargés, ni en
 *    cours de chargement) sont demandés, puis fusionnés via `merge` ;
 *  - une requête dont la plage est sortie de la fenêtre est annulée
 *    (`AbortController`) : une réponse tardive n'écrit jamais de données d'une
 *    période que l'utilisateur a quittée.
 *
 * Un rechargement complet (bloquant ou silencieux) n'accepte pas de segments
 * concurrents : si la fenêtre bouge pendant l'appel, il est annulé et relancé
 * sur la nouvelle fenêtre. Sans cela, sa réponse (qui REMPLACE les données)
 * effacerait un segment arrivé avant elle, que plus rien ne redemanderait.
 *
 * @param {object} opts
 * @param {{from: Date, to: Date}} opts.period fenêtre voulue (dates locales 00:00)
 * @param {string} opts.depsKey  tout ce qui, en dehors des dates, change la réponse
 * @param {number} opts.reloadKey incrément = rechargement complet silencieux
 * @param {(range: {from: string, to: string, signal: AbortSignal}) => Promise<object>} opts.fetchRange
 * @param {(prev: object, next: object) => object} opts.merge fusion d'un segment
 * @param {(data: object, from: string, to: string) => object} opts.prune retire ce qui sort de la fenêtre
 * @param {object} opts.empty valeur initiale
 */
export function useRangeData({ period, depsKey, reloadKey, fetchRange, merge, prune, empty }) {
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState(null);
  // Incrémenté quand une réponse arrive : relance le calcul des segments manquants.
  const [version, setVersion] = useState(0);

  const from = toISODate(period.from);
  const to = toISODate(period.to);
  const windowRef = useRef({ from, to });
  windowRef.current = { from, to };

  // Fonctions fournies par l'appelant : lues au moment de l'appel (elles
  // changent à chaque rendu, les mettre en dépendance relancerait tout).
  const fnRef = useRef({ fetchRange, merge, prune });
  fnRef.current = { fetchRange, merge, prune };

  const stateRef = useRef({
    depsKey: null,
    reloadKey: null,
    loaded: [], // intervalles [from, to] ISO couverts par `data`
    inflight: new Map(), // id -> { from, to, replace, controller }
    seq: 0,
  });

  const refreshFetching = useCallback(() => {
    const st = stateRef.current;
    setFetching(st.inflight.size > 0);
    const blocking = [...st.inflight.values()].some((r) => r.blocking);
    setLoading(blocking);
  }, []);

  const abortAll = useCallback(() => {
    const st = stateRef.current;
    for (const r of st.inflight.values()) r.controller.abort();
    st.inflight.clear();
  }, []);

  const request = useCallback(
    (rangeFrom, rangeTo, { replace, blocking }) => {
      const st = stateRef.current;
      const id = ++st.seq;
      const controller = new AbortController();
      st.inflight.set(id, { from: rangeFrom, to: rangeTo, replace, blocking, controller });
      refreshFetching();
      fnRef.current
        .fetchRange({ from: rangeFrom, to: rangeTo, signal: controller.signal })
        .then((res) => {
          if (controller.signal.aborted) return;
          st.inflight.delete(id);
          const win = windowRef.current;
          const { merge: mergeFn, prune: pruneFn } = fnRef.current;
          setData((prev) => pruneFn(replace ? res : mergeFn(prev, res), win.from, win.to));
          st.loaded = clipIntervals(
            replace ? [[rangeFrom, rangeTo]] : addInterval(st.loaded, [rangeFrom, rangeTo]),
            win.from,
            win.to,
          );
          setError(null);
          setVersion((v) => v + 1);
        })
        .catch((err) => {
          if (controller.signal.aborted || err?.name === 'AbortError') return;
          st.inflight.delete(id);
          setError(err?.message ?? String(err));
        })
        .finally(() => {
          if (!controller.signal.aborted) refreshFetching();
        });
    },
    [refreshFetching],
  );

  useEffect(() => {
    const st = stateRef.current;
    const depsChanged = st.depsKey !== depsKey;
    const reloadChanged = st.reloadKey !== reloadKey;
    const fullInflight = [...st.inflight.values()].find((r) => r.replace);

    if (depsChanged || reloadChanged) {
      st.depsKey = depsKey;
      st.reloadKey = reloadKey;
      abortAll();
      st.loaded = [];
      request(from, to, { replace: true, blocking: depsChanged });
      return;
    }

    if (fullInflight) {
      // Rechargement complet en cours : on le relance sur la nouvelle fenêtre
      // s'il ne la couvre plus (cf. en-tête).
      if (fullInflight.from !== from || fullInflight.to !== to) {
        const { blocking } = fullInflight;
        abortAll();
        request(from, to, { replace: true, blocking });
      }
      return;
    }

    // Glissement : la plage couverte est rognée à la fenêtre… mais les données
    // ne sont élaguées qu'à l'arrivée du segment suivant, dans la même mise à
    // jour que la fusion. Élaguer ici déclenchait un recalcul complet (voies,
    // hauteurs de ligne, segments de chaque personne) de plus à chaque
    // glissement — les saccades en zoom Mois avec tous les consultants.
    st.loaded = clipIntervals(st.loaded, from, to);
    // … on annule les requêtes devenues inutiles…
    let aborted = false;
    for (const [id, r] of st.inflight) {
      if (r.to < from || r.from > to) {
        r.controller.abort();
        st.inflight.delete(id);
        aborted = true;
      }
    }
    if (aborted) refreshFetching();
    // … et on ne demande que les jours ni chargés, ni en cours de chargement.
    const covered = [...st.loaded];
    for (const r of st.inflight.values()) covered.push([r.from, r.to]);
    for (const [gFrom, gTo] of gaps(covered, from, to)) {
      request(gFrom, gTo, { replace: false, blocking: false });
    }
  }, [from, to, depsKey, reloadKey, version, abortAll, request, refreshFetching]);

  // Démontage : aucune réponse ne doit arriver sur un composant démonté. On
  // réinitialise aussi les clés pour qu'un remontage (StrictMode) recharge.
  useEffect(
    () => () => {
      abortAll();
      stateRef.current.depsKey = null;
      stateRef.current.reloadKey = null;
      stateRef.current.loaded = [];
    },
    [abortAll],
  );

  return { data, setData, loading, fetching, error };
}

// ---------- Helpers de plages (dates ISO « YYYY-MM-DD », comparables en texte) ----------

export function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function shiftISO(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  return toISODate(new Date(y, m - 1, d + days));
}

function normalize(intervals) {
  const sorted = intervals.filter(([a, b]) => a <= b).sort((x, y) => (x[0] < y[0] ? -1 : 1));
  const out = [];
  for (const [a, b] of sorted) {
    const last = out[out.length - 1];
    // Fusionne aussi deux intervalles adjacents (jour suivant).
    if (last && a <= shiftISO(last[1], 1)) {
      if (b > last[1]) last[1] = b;
    } else {
      out.push([a, b]);
    }
  }
  return out;
}

function addInterval(intervals, iv) {
  return normalize([...intervals.map((x) => [...x]), [...iv]]);
}

function clipIntervals(intervals, from, to) {
  return normalize(
    intervals
      .map(([a, b]) => [a < from ? from : a, b > to ? to : b])
      .filter(([a, b]) => a <= b),
  );
}

/** Portions de [from, to] non couvertes par `intervals`. */
function gaps(intervals, from, to) {
  const out = [];
  let cursor = from;
  for (const [a, b] of clipIntervals(intervals, from, to)) {
    if (a > cursor) out.push([cursor, shiftISO(a, -1)]);
    const next = shiftISO(b, 1);
    if (next > cursor) cursor = next;
  }
  if (cursor <= to) out.push([cursor, to]);
  return out;
}

// ---------- Helpers de fusion partagés par les vues ----------

/** Une instance (allocation, congé) chevauche-t-elle [from, to] ? */
export function spanInRange(item, from, to) {
  const s = String(item.startDate).slice(0, 10);
  const e = String(item.endDate ?? item.startDate).slice(0, 10);
  return e >= from && s <= to;
}

/** Une donnée datée (saisie, statut…) tombe-t-elle dans [from, to] ? */
export function dateInRange(item, from, to) {
  const d = String(item.date).slice(0, 10);
  return d >= from && d <= to;
}

/** Union de deux listes par clé ; la version la plus récente l'emporte. */
export function mergeByKey(prev = [], next = [], keyOf) {
  if (!prev.length) return next;
  if (!next.length) return prev;
  // Seules les clés du segment reçu peuvent être en double : on les indexe, pas
  // toute la fenêtre (des dizaines de milliers de statuts de présence).
  const nextKeys = new Set();
  for (const it of next) nextKeys.add(keyOf(it));
  const out = [];
  for (const it of prev) if (!nextKeys.has(keyOf(it))) out.push(it);
  for (const it of next) out.push(it);
  return out;
}

/** Fusion de `holidaysByPerson` ({ [personId]: [{ date, … }] }). */
export function mergeHolidays(prev = {}, next = {}) {
  const out = { ...prev };
  for (const [pid, list] of Object.entries(next ?? {})) {
    out[pid] = mergeByKey(prev?.[pid] ?? [], list ?? [], (h) => String(h.date).slice(0, 10));
  }
  return out;
}

export function pruneHolidays(holidays = {}, from, to) {
  const out = {};
  for (const [pid, list] of Object.entries(holidays ?? {})) {
    out[pid] = (list ?? []).filter((h) => dateInRange(h, from, to));
  }
  return out;
}

/**
 * Personnes de deux segments. Identiques → on garde la liste précédente (évite
 * de recalculer voies et hauteurs de ligne). Sinon union triée : avec un filtre
 * « côté affaire », le serveur ne renvoie que les personnes ACTIVES dans le
 * segment demandé (ticket #28), la fenêtre en est la réunion.
 */
export function mergePersons(prev = [], next = []) {
  if (!prev.length) return next;
  const nextIds = new Set(next.map((p) => p.id));
  const extra = prev.filter((p) => !nextIds.has(p.id));
  if (!extra.length) return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
  return [...next, ...extra].sort((a, b) =>
    `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'fr', {
      sensitivity: 'base',
    }),
  );
}
